import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../services/api';
import useResource from '../services/useResource';
import { CalendarData, CalendarPost } from '../types/api';
import { Icon } from '../components/icons';
import DayEditor, { deliveryLabel, needsAttention } from '../components/Calendar/DayEditor';
import { addDays, dateLabel, dayDate, weekGrid, weekStart, postingInstant, pstDay } from '../components/Calendar/calendarDates';
import './CalendarPage.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const EMPTY: CalendarData = { start: '', end: '', posts: [], accounts: [] };

export default function CalendarPage() {
  const [firstDay, setFirstDay] = useState(() => weekStart(pstDay(new Date())));
  const cells = useMemo(() => weekGrid(firstDay), [firstDay]);
  const start = cells[0];
  const end = addDays(cells[27], 1);
  const [editingDay, setEditingDay] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [moving, setMoving] = useState<CalendarPost | null>(null);
  const [moveBusy, setMoveBusy] = useState(false);
  const dragging = useRef<CalendarPost | null>(null);
  const { data, loading, error, reload, setData } = useResource<CalendarData>(
    () => apiClient.get<CalendarData>(`/posts/calendar/?start=${start}&end=${end}`), [start, end], { initial: EMPTY },
  );
  // Refresh delivery state while the calendar is visible, including after
  // returning to a tab left open overnight. No browser needed for dispatch.
  useEffect(() => {
    const refresh = () => { if (!document.hidden && !editingDay) void reload(); };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [reload, editingDay]);

  const currentRange = data.start === start && data.end === end;
  const posts = currentRange ? data.posts : EMPTY.posts;
  const today = pstDay(new Date());
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarPost[]>();
    for (const post of posts) {
      const timestamp = post.scheduled_at || post.posted_at;
      const day = post.calendar_day || (timestamp ? pstDay(timestamp) : null);
      if (day) map.set(day, [...(map.get(day) || []), post]);
    }
    return map;
  }, [posts]);
  const editingPost = posts.find((p) => p.calendar_day === editingDay);
  const ready = currentRange && !loading && !error && !moveBusy;
  const issues = posts.filter(needsAttention);

  function saved(post: CalendarPost | null, message?: string) {
    setData((previous) => ({ ...previous, posts: [
      ...previous.posts.filter((p) => p.calendar_day !== editingDay), ...(post ? [post] : []),
    ] }));
    setNotice(message || (post ? 'Post saved.' : 'Post removed from schedule.'));
    setEditingDay(null);
    void reload();
  }

  async function movePost(post: CalendarPost, day: string) {
    if (!post.calendar_day || post.calendar_day === day || moveBusy) return;
    setMoveBusy(true); setNotice('');
    try {
      const target = posts.find((p) => p.calendar_day === day);
      await apiClient.post('/posts/calendar/move/', {
        post_id: post.id, source_day: post.calendar_day, target_day: day, target_post_id: target?.id || null,
      });
      setMoving(null);
      await reload();
      setNotice(target ? 'Posts swapped. Posts moved into past times stay unscheduled.' : `Post moved to ${dateLabel(day)}. Already delivered accounts will not be sent again.`);
    } catch (err) { setNotice((err as Error).message); }
    finally { setMoveBusy(false); dragging.current = null; }
  }

  return <div className="calendar-page">
    {error && <div className="banner banner--error" role="alert">
      Could not load calendar: {error.message} <button className="btn btn--ghost btn--sm" onClick={() => { void reload(); }}>Retry</button>
    </div>}
    {notice && <p className="banner banner--info" role="status">{notice}</p>}
    {moving && <div className="banner banner--info" role="status">Choose a destination day. Occupied days swap places.
      <button className="btn btn--ghost btn--sm" disabled={moveBusy} onClick={() => setMoving(null)}>Cancel move</button>
    </div>}
    {!ready && !error && <p className="muted" role="status">{moveBusy ? 'Moving post…' : 'Loading calendar…'}</p>}
    <div className="calendar-grid" aria-label="Posting calendar" aria-busy={!ready}>
      {WEEKDAYS.map((day) => <div className="calendar-weekday" key={day}>{day}</div>)}
      {cells.map((day) => {
        const dayPosts = byDay.get(day) || [];
        const daily = dayPosts.find((p) => p.calendar_day === day);
        const other = dayPosts.filter((p) => p !== daily);
        const media = daily?.media[0];
        const past = day < today;
        const todayPassed = day === today && postingInstant(day, '11:00') <= Date.now();
        const state = daily ? deliveryLabel(daily) : past ? '' : todayPassed ? 'Pick a time' : 'Add post';
        return <div key={day}
          onDragOver={(event) => { if (dragging.current && ready) { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; } }}
          onDrop={(event) => { event.preventDefault(); const post = dragging.current; if (post && ready) void movePost(post, day); }}
          className={`calendar-cell${day === today ? ' is-today' : ''}`}>
          <button className={`calendar-day-button${daily ? ' has-post' : ''}${daily && needsAttention(daily) ? ' needs-attention' : ''}`}
            draggable={ready && !!daily && daily.schedule_state !== 'processing'}
            onDragStart={(event) => { if (!daily) return; dragging.current = daily; event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', String(daily.id)); }}
            onDragEnd={() => { dragging.current = null; }}
            disabled={!ready || (!daily && past && !moving)} onClick={() => { if (moving) { void movePost(moving, day); } else { setNotice(''); setEditingDay(day); } }}
            aria-label={`${dateLabel(day)}${state ? `: ${state}` : ''}`}>
            <span className="calendar-cell-day">{Number(day.slice(-2))}{(day === start || day.endsWith('-01')) && <small> {dayDate(day).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short' })}</small>}</span>
            {media && <span className="calendar-thumbnail">
              {media.media_type === 'video' ? <video draggable={false} src={`${media.file}#t=0.1`} muted playsInline preload="metadata" /> : <img draggable={false} src={media.file} alt="" loading="lazy" />}
            </span>}
            {['Posted', 'Posting', 'Queued', 'Needs attention'].includes(state) &&
              <span className={`calendar-status-icon calendar-status-icon--${state === 'Posted' ? 'posted' : state === 'Needs attention' ? 'error' : 'posting'}`}
                role="img" aria-label={state} title={state}>
                <Icon name={state === 'Posted' ? 'completed' : state === 'Needs attention' ? 'close' : 'posting'} />
              </span>}
          </button>
          {other.map((post) => <Link className="calendar-existing" key={post.id} to={`/create?postId=${post.id}`}>
            {deliveryLabel(post)}
          </Link>)}
        </div>;
      })}
    </div>
      <nav className="calendar-nav" aria-label="Calendar weeks">
        <button className="btn btn--sheen" aria-label="Previous four weeks" onClick={() => setFirstDay(addDays(firstDay, -28))}><Icon name="previous" /> Previous</button>
        <button className="btn btn--sheen" onClick={() => setFirstDay(weekStart(today))}>Today</button>
        <button className="btn btn--sheen" aria-label="Next four weeks" onClick={() => setFirstDay(addDays(firstDay, 28))}>Next <Icon name="next" /></button>
      </nav>
    {issues.length > 0 && <details className="panel calendar-attention">
      <summary>Needs attention ({issues.length})</summary>
      {issues.map((post) => <details className="calendar-error-details" key={post.id}>
        <summary>{post.calendar_day ? dateLabel(post.calendar_day) : 'Scheduled post'} — delivery details</summary>
        <p>{post.delivery_warning || post.schedule_error || 'Some destinations have not received this post.'}</p>
        {post.calendar_day ? <button className="btn btn--ghost btn--sm" onClick={() => setEditingDay(post.calendar_day)}>Open post / retry</button>
          : <Link to="/dashboard">View delivery details</Link>}
      </details>)}
    </details>}
    {editingDay && <DayEditor key={editingDay} day={editingDay} post={editingPost}
      accounts={data.accounts} onClose={() => setEditingDay(null)} onSaved={saved}
      onMove={() => { if (editingPost) { setMoving(editingPost); setEditingDay(null); } }} />}


  </div>;
}
